/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	MultiplayerStateComponent,
	SharedWorldComponent,
} from '../../components/MultiplayerComponents';
import {
	ensureSignedIn,
	isFirebaseConfigured,
} from '../../lib/firebase/firebaseClient';

import { System } from 'ecsy';
import { ensurePlotForUser } from '../../lib/firebase/PlotStorage';
import { getOnlyEntity } from '../../utils/entityUtils';

/**
 * Foundation of the persistent shared garden world (design Phase 1).
 *
 * On startup this system establishes the player's durable identity and plot:
 *   1. Anonymous Firebase sign-in -> a stable `uid` (persisted in IndexedDB).
 *   2. First visit for that uid -> atomically allocate a plot and write its
 *      Firestore docs; returning visits reuse the same plot.
 * The resulting uid / plotId / plotIndex are published on a single
 * SharedWorldComponent for the later world-load, persist and live-sync phases.
 *
 * When Firebase is not configured (config still holds REPLACE_ME), the whole
 * layer stays dormant: `configured` is false, nothing touches the network, and
 * the game runs exactly as the single-player local-save experience.
 */
export class SharedWorldSystem extends System {
	init() {
		const entity = this.world.createEntity();
		entity.addComponent(SharedWorldComponent, {
			configured: isFirebaseConfigured(),
		});
		this._sharedWorld = entity.getMutableComponent(SharedWorldComponent);
		this._bootstrapped = false;

		if (!this._sharedWorld.configured) {
			console.info(
				'[SharedWorld] Firebase not configured — running single-player ' +
					'(local save). Fill src/js/config/firebaseConfig.js to enable the ' +
					'shared world.',
			);
			this._bootstrapped = true;
		}
	}

	/**
	 * Establish identity + plot. Runs once, as soon as the multiplayer profile
	 * (name / country) is available so the plot's denormalized display fields are
	 * populated on first allocation.
	 */
	async _bootstrap() {
		this._bootstrapped = true;
		try {
			const user = await ensureSignedIn();
			if (!user) return; // config was cleared mid-flight; stay dormant.

			const multiplayerState = getOnlyEntity(
				this.queries.multiplayer,
				false,
			)?.getComponent(MultiplayerStateComponent);

			const { plotId, plotIndex } = await ensurePlotForUser(user.uid, {
				displayName: multiplayerState?.localPlayerName,
				country: multiplayerState?.localPlayerCountry,
			});

			this._sharedWorld.uid = user.uid;
			this._sharedWorld.plotId = plotId;
			this._sharedWorld.plotIndex = plotIndex;
			this._sharedWorld.ready = true;

			console.info(
				`[SharedWorld] Signed in as ${user.uid} — plot ${plotId} ` +
					`(index ${plotIndex}).`,
			);
		} catch (e) {
			console.error('[SharedWorld] Failed to establish identity/plot', e);
		}
	}

	execute() {
		if (this._bootstrapped) return;
		// Wait until the multiplayer profile has been created so we can pass the
		// player's name/country into plot allocation; then bootstrap exactly once.
		const multiplayerEntity = getOnlyEntity(this.queries.multiplayer, false);
		if (!multiplayerEntity) return;
		this._bootstrap();
	}
}

SharedWorldSystem.queries = {
	multiplayer: { components: [MultiplayerStateComponent] },
	sharedWorld: { components: [SharedWorldComponent] },
};
